import { QueryKey, UseQueryResult } from '@tanstack/react-query';

import {
  apiSeoLocationCityFaqsList,
  getApiSeoLocationCityFaqsListQueryKey,
  useApiSeoLocationCityFaqsList,
} from '@rover/rsdk/src/apiClient/latest/api-seo-location-city-faqs-list';
import {
  apiSeoLocationCityRetrieve,
  getApiSeoLocationCityRetrieveQueryKey,
  useApiSeoLocationCityRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-seo-location-city-retrieve';
import {
  apiSeoLocationCityReviewsList,
  getApiSeoLocationCityReviewsListQueryKey,
  useApiSeoLocationCityReviewsList,
} from '@rover/rsdk/src/apiClient/latest/api-seo-location-city-reviews-list';
import {
  apiSeoLocationNeighborhoodFaqsList,
  getApiSeoLocationNeighborhoodFaqsListQueryKey,
  useApiSeoLocationNeighborhoodFaqsList,
} from '@rover/rsdk/src/apiClient/latest/api-seo-location-neighborhood-faqs-list';
import {
  apiSeoLocationNeighborhoodRetrieve,
  getApiSeoLocationNeighborhoodRetrieveQueryKey,
  useApiSeoLocationNeighborhoodRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-seo-location-neighborhood-retrieve';
import {
  apiSeoLocationNeighborhoodReviewsList,
  getApiSeoLocationNeighborhoodReviewsListQueryKey,
  useApiSeoLocationNeighborhoodReviewsList,
} from '@rover/rsdk/src/apiClient/latest/api-seo-location-neighborhood-reviews-list';
import type { ApiSeoLocationCityReviewsListParams } from '@rover/rsdk/src/apiClient/latest/model/apiSeoLocationCityReviewsListParams';
import type { ApiSeoLocationNeighborhoodReviewsListParams } from '@rover/rsdk/src/apiClient/latest/model/apiSeoLocationNeighborhoodReviewsListParams';
import { LocalityTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/localityTypeEnum';
import type { SeoLocation } from '@rover/rsdk/src/apiClient/latest/model/seoLocation';
import { ErrorType } from '@rover/rsdk/src/modules/Network/orvalHTTPInterface';
import { SeoLocationDescriptor } from '@rover/utilities/seo';

type UseApiClientHookResponse<T extends (...args: any) => any> = UseQueryResult<
  Awaited<ReturnType<T>>,
  ErrorType<unknown>
> & {
  queryKey: QueryKey;
};

const neighborhoodServiceArgs = ({
  countryUrlPrefix,
  language,
  neighborhoodSlug,
  citySlug,
  regionSlug,
  serviceTypeSlug,
}: SeoLocationDescriptor) =>
  [
    countryUrlPrefix,
    language,
    neighborhoodSlug || '',
    citySlug,
    regionSlug,
    serviceTypeSlug,
  ] as const;

const cityServiceArgs = ({
  countryUrlPrefix,
  language,
  citySlug,
  regionSlug,
  serviceTypeSlug,
}: SeoLocationDescriptor) =>
  [countryUrlPrefix, language, citySlug, regionSlug, serviceTypeSlug] as const;

// unnecessary param, but required by the urlpattern
const EXTRA = '';

type UseSearchSeoLocationResponse = UseApiClientHookResponse<
  typeof apiSeoLocationCityRetrieve | typeof apiSeoLocationNeighborhoodRetrieve
>;

export const useApiSearchSeoLocationRetrieve = (
  seoLocationDescriptor: SeoLocationDescriptor
): UseSearchSeoLocationResponse => {
  const isNeighborhood = !!seoLocationDescriptor.neighborhoodSlug;
  const cityQuery = useApiSeoLocationCityRetrieve(
    ...cityServiceArgs(seoLocationDescriptor),
    EXTRA,
    {
      query: { enabled: !isNeighborhood },
    }
  );
  const neighborhoodQuery = useApiSeoLocationNeighborhoodRetrieve(
    ...neighborhoodServiceArgs(seoLocationDescriptor),
    EXTRA,
    {
      query: { enabled: isNeighborhood },
    }
  );
  return isNeighborhood ? neighborhoodQuery : cityQuery;
};

export const getApiSearchSeoLocationPrefetchQueryOptions = (
  seoLocationDescriptor: SeoLocationDescriptor
): {
  queryKey: readonly string[];
  queryFn: () => Promise<SeoLocation>;
} => {
  if (seoLocationDescriptor.neighborhoodSlug) {
    const args = neighborhoodServiceArgs(seoLocationDescriptor);
    return {
      queryKey: getApiSeoLocationNeighborhoodRetrieveQueryKey(...args, EXTRA),
      queryFn: () => apiSeoLocationNeighborhoodRetrieve(...args, EXTRA),
    };
  }
  const args = cityServiceArgs(seoLocationDescriptor);
  return {
    queryKey: getApiSeoLocationCityRetrieveQueryKey(...args, EXTRA),
    queryFn: () => apiSeoLocationCityRetrieve(...args, EXTRA),
  };
};

export const useApiSearchSeoLocationReviewsList = (
  seoLocationDescriptor: SeoLocationDescriptor
): UseApiClientHookResponse<
  typeof apiSeoLocationCityReviewsList | typeof apiSeoLocationNeighborhoodReviewsList
> => {
  const isNeighborhood = !!seoLocationDescriptor.neighborhoodSlug;
  const neighborhoodQuery = useApiSeoLocationNeighborhoodReviewsList(
    ...neighborhoodServiceArgs(seoLocationDescriptor),
    {},
    { query: { enabled: isNeighborhood } }
  );
  const cityQuery = useApiSeoLocationCityReviewsList(
    ...cityServiceArgs(seoLocationDescriptor),
    {},
    { query: { enabled: !isNeighborhood } }
  );
  return isNeighborhood ? neighborhoodQuery : cityQuery;
};

export const getApiSearchSeoLocationReviewsListPrefetchQueryOptions = (
  seoLocationDescriptor: SeoLocationDescriptor
): {
  queryKey: ReadonlyArray<
    string | ApiSeoLocationNeighborhoodReviewsListParams | ApiSeoLocationCityReviewsListParams
  >;
  queryFn: () => Promise<unknown>;
} => {
  if (seoLocationDescriptor.neighborhoodSlug) {
    const args = neighborhoodServiceArgs(seoLocationDescriptor);
    return {
      queryKey: getApiSeoLocationNeighborhoodReviewsListQueryKey(...args, {}),
      queryFn: () => apiSeoLocationNeighborhoodReviewsList(...args),
    };
  }
  const args = cityServiceArgs(seoLocationDescriptor);
  return {
    queryKey: getApiSeoLocationCityReviewsListQueryKey(...args, {}),
    queryFn: () => apiSeoLocationCityReviewsList(...args),
  };
};

export const useApiSearchSeoLocationFaqsList = (
  seoLocationDescriptor: SeoLocationDescriptor,
  localityType: LocalityTypeEnum
): UseApiClientHookResponse<
  typeof apiSeoLocationCityFaqsList | typeof apiSeoLocationNeighborhoodFaqsList
> => {
  const neighborhoodQuery = useApiSeoLocationNeighborhoodFaqsList(
    ...neighborhoodServiceArgs(seoLocationDescriptor),
    { query: { enabled: localityType === LocalityTypeEnum.neighborhood } }
  );
  const cityQuery = useApiSeoLocationCityFaqsList(...cityServiceArgs(seoLocationDescriptor), {
    query: { enabled: localityType === LocalityTypeEnum.city },
  });
  return localityType === LocalityTypeEnum.neighborhood ? neighborhoodQuery : cityQuery;
};

export const getApiSearchSeoLocationFaqsListPrefetchQueryOptions = (
  seoLocationDescriptor: SeoLocationDescriptor,
  localityType: LocalityTypeEnum
): {
  queryKey: readonly string[];
  queryFn: () => Promise<unknown>;
} => {
  if (localityType === LocalityTypeEnum.neighborhood) {
    const args = neighborhoodServiceArgs(seoLocationDescriptor);
    return {
      queryKey: getApiSeoLocationNeighborhoodFaqsListQueryKey(...args),
      queryFn: () => apiSeoLocationNeighborhoodFaqsList(...args),
    };
  }
  const args = cityServiceArgs(seoLocationDescriptor);
  return {
    queryKey: getApiSeoLocationCityFaqsListQueryKey(...args),
    queryFn: () => apiSeoLocationCityFaqsList(...args),
  };
};
